import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getModels, getMyCapabilities, isAuthenticated } from '../api'
import { getRealm } from '../lib/realm'

const MODEL_SCOPE_STORAGE_KEY = 'fanltv:v2:model-scope'

const WorkspaceScopeContext = createContext({
  loading: true,
  fanvueReviewWorkspace: false,
  capabilities: null,
  models: [],
  selectedModelId: '',
  selectedModel: null,
  setSelectedModelId: () => {},
  refreshModels: async () => {},
})

export function WorkspaceScopeProvider({ children }) {
  useLocation()
  const authenticated = isAuthenticated(getRealm())
  const [state, setState] = useState({
    loading: authenticated,
    fanvueReviewWorkspace: false,
    capabilities: null,
    models: [],
  })
  const [selectedModelId, setSelectedModelIdState] = useState(() => {
    if (typeof localStorage === 'undefined') return ''
    return localStorage.getItem(MODEL_SCOPE_STORAGE_KEY) || ''
  })

  async function refreshModels() {
    if (!authenticated) return []
    const result = await getModels()
    const rows = Array.isArray(result)
      ? result
      : Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.items)
          ? result.items
          : []
    setState((current) => ({ ...current, models: rows }))
    setSelectedModelIdState((current) => (
      current && !rows.some((model) => String(model.id) === String(current))
        ? ''
        : current
    ))
    return rows
  }

  useEffect(() => {
    if (!authenticated) {
      setState({ loading: false, fanvueReviewWorkspace: false, capabilities: null, models: [] })
      return undefined
    }

    let alive = true
    setState((current) => ({ ...current, loading: true }))
    Promise.allSettled([getMyCapabilities(), getModels()])
      .then(([capabilitiesResult, modelsResult]) => {
        if (!alive) return
        const capabilities = capabilitiesResult.status === 'fulfilled'
          ? capabilitiesResult.value
          : null
        const modelPayload = modelsResult.status === 'fulfilled' ? modelsResult.value : []
        const models = Array.isArray(modelPayload)
          ? modelPayload
          : Array.isArray(modelPayload?.data)
            ? modelPayload.data
            : Array.isArray(modelPayload?.items)
              ? modelPayload.items
              : []
        setState({
          loading: false,
          fanvueReviewWorkspace: capabilities?.fanvue_review_workspace === true,
          capabilities,
          models,
        })
        setSelectedModelIdState((current) => (
          current && !models.some((model) => String(model.id) === String(current))
            ? ''
            : current
        ))
      })
      .catch(() => {
        if (alive) setState({ loading: false, fanvueReviewWorkspace: false, capabilities: null, models: [] })
      })
    return () => { alive = false }
  }, [authenticated])

  function setSelectedModelId(value) {
    const normalized = value == null ? '' : String(value)
    setSelectedModelIdState(normalized)
    if (typeof localStorage !== 'undefined') {
      if (normalized) localStorage.setItem(MODEL_SCOPE_STORAGE_KEY, normalized)
      else localStorage.removeItem(MODEL_SCOPE_STORAGE_KEY)
    }
  }

  const selectedModel = useMemo(
    () => state.models.find((model) => String(model.id) === String(selectedModelId)) || null,
    [state.models, selectedModelId],
  )
  const value = useMemo(() => ({
    ...state,
    selectedModelId,
    selectedModel,
    setSelectedModelId,
    refreshModels,
  }), [state, selectedModelId, selectedModel])
  return <WorkspaceScopeContext.Provider value={value}>{children}</WorkspaceScopeContext.Provider>
}

export function useWorkspaceScope() {
  return useContext(WorkspaceScopeContext)
}
